import type { Student, Constraints, Settings, ConstraintSet } from "../types/models";

export interface ValidationResult {
    ok: boolean;
    errors: string[];
}

/**
 * Simplified constraint validation
 * All constraints are now treated as HARD (must be enforced)
 */
export function validateConstraints(
    students: Student[],
    constraints: Constraints,
    settings: Settings
): ValidationResult {
    const errors: string[] = [];
    const studentIds = new Set(students.map(s => s.id));

    // 1. Check all referenced students exist
    for (const set of [...constraints.mustTogether, ...constraints.mustApart]) {
        for (const id of set.ids) {
            if (!studentIds.has(id)) {
                errors.push(`Unknown student ID: ${id}`);
            }
        }
    }

    // 2. Check must-together sets fit in maxSize
    for (const set of constraints.mustTogether) {
        if (set.ids.length > settings.maxSize) {
            const names = set.ids
                .map(id => students.find(s => s.id === id)?.name ?? id)
                .join(", ");
            errors.push(
                `Must-together set [${names}] has ${set.ids.length} students ` +
                `but max group size is ${settings.maxSize}`
            );
        }
    }

    // 3. Check for conflicts (same pair in both together and apart)
    const togetherPairs = getPairs(constraints.mustTogether);
    const apartPairs = getPairs(constraints.mustApart);

    for (const pair of togetherPairs) {
        if (apartPairs.has(pair)) {
            const [a, b] = pair.split('|');
            const nameA = students.find(s => s.id === a)?.name ?? a;
            const nameB = students.find(s => s.id === b)?.name ?? b;
            errors.push(
                `Conflict: ${nameA} and ${nameB} are marked as both ` +
                `must-together AND must-apart`
            );
        }
    }

    // 4. Check maxGroups feasibility
    if (settings.maxGroups) {
        const minGroupsNeeded = Math.ceil(students.length / settings.maxSize);
        if (minGroupsNeeded > settings.maxGroups) {
            errors.push(
                `Need at least ${minGroupsNeeded} groups for ${students.length} students ` +
                `with max size ${settings.maxSize}, but max groups is ${settings.maxGroups}`
            );
        }
    }

    return { ok: errors.length === 0, errors };
}

/**
 * Get all pairwise combinations from constraint sets
 */
function getPairs(sets: ConstraintSet[]): Set<string> {
    const pairs = new Set<string>();

    for (const set of sets) {
        for (let i = 0; i < set.ids.length; i++) {
            for (let j = i + 1; j < set.ids.length; j++) {
                const idI = set.ids[i];
                const idJ = set.ids[j];
                if (idI && idJ) {
                    const [a, b] = [idI, idJ].sort();
                    pairs.add(`${a}|${b}`);
                }
            }
        }
    }

    return pairs;
}

/**
 * Merge transitive must-together constraints
 * If A→B and B→C, merge into {A,B,C}
 */
export function mergeTransitiveMustTogether(constraints: Constraints): Array<{ ids: string[] }> {
    const sets: Array<Set<string>> = [];

    for (const set of constraints.mustTogether) {
        let merged: Set<string> | null = null;
        const toRemove: number[] = [];

        // Find all sets that share any student with this set
        for (let i = 0; i < sets.length; i++) {
            const existing = sets[i];
            if (!existing) continue;
            if (set.ids.some(id => existing.has(id))) {
                if (!merged) {
                    merged = existing;
                    // Add all from current set
                    set.ids.forEach(id => merged!.add(id));
                } else {
                    // Merge existing into merged
                    existing.forEach(id => merged!.add(id));
                    toRemove.push(i);
                }
            }
        }

        // Remove merged sets in reverse order to preserve indices
        for (let i = toRemove.length - 1; i >= 0; i--) {
            const indexToRemove = toRemove[i];
            if (indexToRemove !== undefined) {
                sets.splice(indexToRemove, 1);
            }
        }

        if (!merged) {
            // No overlap found, create new set
            sets.push(new Set(set.ids));
        }
    }

    return sets.map(s => ({ ids: Array.from(s) }));
}

/**
 * Build set of must-apart pairs for quick lookup
 */
export function buildApartPairs(constraints: Constraints): Set<string> {
    return getPairs(constraints.mustApart);
}
